import api, { API_URL } from './api';

class PushService {
  constructor() {
    this.vapidPublicKey = null;
    this.registration = null;
    this.subscription = null;
    this.unregistering = false; // Flag para prevenir múltiplas chamadas simultâneas
  }

  /**
   * Verifica se o navegador suporta push notifications
   */
  isSupported() {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Solicita permissão para notificações
   */
  async requestPermission() {
    if (!this.isSupported()) {
      console.warn('Push notifications não são suportadas neste navegador');
      return 'unsupported';
    }

    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.warn('Permissão para notificações negada ou não concedida:', permission);
      return permission; // Retorna 'denied' ou 'default' sem lançar erro
    }

    return permission;
  }

  /**
   * Busca a chave pública VAPID do backend
   */
  async getVapidPublicKey() {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await api.get('/push/vapid-public-key');
      this.vapidPublicKey = response.publicKey;
      return this.vapidPublicKey;
    } catch (error) {
      // Se for erro de autenticação, não tentar novamente
      if (error.isAuthError || error.response?.status === 401) {
        console.warn('⚠️ Erro de autenticação ao buscar VAPID public key. Push notifications não serão ativadas.');
        return null;
      }
      console.error('Erro ao buscar VAPID public key:', error);
      throw error;
    }
  }

  /**
   * Registra o service worker
   */
  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker não é suportado');
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      this.registration = registration;
      
      // Aguardar o service worker estar pronto
      await navigator.serviceWorker.ready;
      
      return registration;
    } catch (error) {
      console.error('Erro ao registrar service worker:', error);
      throw error;
    }
  }

  /**
   * Converte a chave VAPID base64 para Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Cria uma subscription e envia para o backend
   */
  async registerSubscription() {
    try {
      // 1. Verificar suporte
      if (!this.isSupported()) {
        console.warn('Push notifications não são suportadas');
        return null;
      }

      // 2. Solicitar permissão
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.log('Permissão de notificações não concedida. Push notifications não serão ativadas.');
        return null;
      }

      // 3. Registrar service worker
      if (!this.registration) {
        await this.registerServiceWorker();
      }

      // 4. Buscar chave pública VAPID
      const vapidPublicKey = await this.getVapidPublicKey();
      const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

      // 5. Criar subscription
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      this.subscription = subscription;

      // 6. Converter subscription para formato enviável
      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: this.arrayBufferToBase64(subscription.getKey('auth'))
        }
      };

      // 7. Enviar para o backend
      await api.post('/push/register', subscriptionData);

      console.log('✅ Push subscription registrada com sucesso');
      return subscription;
    } catch (error) {
      // Se for erro de autenticação, não tentar novamente
      if (error.isAuthError || error.response?.status === 401) {
        console.warn('⚠️ Erro de autenticação ao registrar push subscription. Push notifications não serão ativadas.');
        return null;
      }
      console.error('Erro ao registrar push subscription:', error);
      throw error;
    }
  }

  /**
   * Converte ArrayBuffer para base64
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Remove a subscription do backend
   */
  async unregisterSubscription() {
    // Prevenir múltiplas chamadas simultâneas
    if (this.unregistering) {
      console.log('⚠️ Unregister já em andamento, ignorando chamada duplicada');
      return;
    }

    // Se já não há subscription, não fazer nada
    if (!this.subscription && !this.registration) {
      return;
    }

    this.unregistering = true;

    try {
      if (!this.subscription) {
        // Tentar buscar subscription atual
        if (this.registration) {
          const subscription = await this.registration.pushManager.getSubscription();
          if (subscription) {
            this.subscription = subscription;
          }
        }
      }

      if (this.subscription) {
        const endpoint = this.subscription.endpoint;
        
        // Remover do backend (ignorar erro 401 - usuário já não autenticado)
        try {
          await api.post('/push/unregister', { endpoint });
        } catch (error) {
          // Se for erro de autenticação (401), não é crítico - usuário já está deslogando
          if (error.isAuthError || error.response?.status === 401) {
            console.log('ℹ️ Não foi possível remover subscription do backend (não autenticado), mas continuando...');
          } else {
            throw error;
          }
        }

        // Remover do navegador
        try {
          await this.subscription.unsubscribe();
        } catch (unsubError) {
          console.warn('⚠️ Erro ao fazer unsubscribe do navegador:', unsubError);
        }
        
        this.subscription = null;

        console.log('✅ Push subscription removida com sucesso');
      }
    } catch (error) {
      // Não logar erro crítico se for 401 - é esperado durante logout
      if (!error.isAuthError && error.response?.status !== 401) {
        console.error('Erro ao remover push subscription:', error);
      }
      // Não lançar erro para não bloquear o logout
    } finally {
      this.unregistering = false;
    }
  }

  /**
   * Inicializa o serviço de push (chamar após login)
   */
  async initialize() {
    try {
      if (!this.isSupported()) {
        console.log('ℹ️ Push notifications não são suportadas');
        return false;
      }

      // Verificar se já existe uma subscription
      if (this.registration) {
        const existingSubscription = await this.registration.pushManager.getSubscription();
        if (existingSubscription) {
          this.subscription = existingSubscription;
          console.log('ℹ️ Subscription existente encontrada');
          return true;
        }
      } else {
        await this.registerServiceWorker();
      }

      // Tentar registrar nova subscription
      const subscription = await this.registerSubscription();
      // Se retornar null (erro de auth), não retornar erro, apenas desabilitar push
      return subscription !== null;
    } catch (error) {
      // Se for erro de autenticação, não tentar novamente
      if (error.isAuthError || error.response?.status === 401) {
        console.warn('⚠️ Erro de autenticação ao inicializar push service. Push notifications desabilitadas.');
        return false;
      }
      console.error('Erro ao inicializar push service:', error);
      // Não bloquear o app se falhar
      return false;
    }
  }

  /**
   * Atualiza a subscription (útil quando expira)
   */
  async updateSubscription() {
    try {
      // Remover subscription antiga
      await this.unregisterSubscription();
      
      // Criar nova
      await this.registerSubscription();
    } catch (error) {
      console.error('Erro ao atualizar subscription:', error);
      throw error;
    }
  }
}

// Exportar instância singleton
const pushService = new PushService();
export default pushService;

